import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { api } from "../api/client.js";
import { SituationRow } from "../components/SituationRow.js";

/**
 * One company, and what Mosaic knows about it: above all its separation history in both
 * directions — has it run a separation, and was it itself one, which is what its own
 * `10-12B` says. A situation page answers neither; these are questions about a company
 * across transactions.
 *
 * The situations come from the radar's list endpoint through a `cik` filter, so a
 * situation looks the same wherever it is listed and "which situations count" is decided
 * in one place.
 */
export function CompanyPage() {
  const { cik = "" } = useParams();
  const company = useQuery({ queryKey: ["company", cik], queryFn: () => api.company(cik) });
  const situations = useQuery({
    queryKey: ["situations", "company", cik],
    queryFn: () => api.situations({ cik, limit: 50 }),
  });
  const filings = useQuery({
    queryKey: ["companyFilings", cik],
    queryFn: () => api.companyFilings(cik),
  });

  if (company.isLoading) {
    return (
      <div className="read">
        <div className="empty">
          <p>Loading…</p>
        </div>
      </div>
    );
  }
  if (!company.data) {
    return (
      <div className="read">
        <div className="empty">
          <p>No company with CIK {cik}.</p>
          <p className="hint">
            Mosaic knows a company once it has ingested a filing by it. Nothing here yet.
          </p>
        </div>
      </div>
    );
  }

  const c = company.data;
  const current = c.tickers.filter((t) => t.isCurrent);
  const rows = situations.data?.items ?? [];
  const asParent = rows.filter((s) => s.parent.cik === c.cik);
  const asSpinco = rows.filter((s) => s.spinco.cik === c.cik);

  return (
    <div className="read">
      <article className="article">
        <div className="eyebrow" style={{ ["--sc" as string]: "var(--s1)" }}>
          <i />
          <span>Company</span>
          <span>·</span>
          <span>CIK {c.cik}</span>
          {c.sic && (
            <>
              <span>·</span>
              <span>SIC {c.sic}</span>
            </>
          )}
        </div>
        <h1>{c.name}</h1>
        {c.sicDescription && <p className="deck">{c.sicDescription}.</p>}

        <div className="byline">
          {current.length > 0 && (
            <span>
              <b>{current.map((t) => t.ticker).join(" · ")}</b>
              {current[0]?.exchange ? ` · ${current[0].exchange}` : ""}
            </span>
          )}
          {c.stateOfIncorporation && (
            <span>
              incorporated <b>{c.stateOfIncorporation}</b>
            </span>
          )}
          {filings.data && (
            <span>
              <b>{filings.data.total}</b> filing{filings.data.total === 1 ? "" : "s"} held
            </span>
          )}
        </div>

        {/*
          Both directions, and each absence is stated rather than left as an empty
          heading. "Has never run one" is an answer to the question the page exists for;
          a missing section is not.
        */}
        <div className="sechd">Separation history</div>
        <div className="sephist">
          <div className="sepside">
            <div className="sepk">As parent</div>
            {asParent.length > 0 ? (
              asParent.map((s) => <SituationRow key={s.id} situation={s} />)
            ) : (
              <p className="sepnone">Has not separated a business that Mosaic has seen.</p>
            )}
          </div>
          <div className="sepside">
            <div className="sepk">As SpinCo</div>
            {asSpinco.length > 0 ? (
              asSpinco.map((s) => <SituationRow key={s.id} situation={s} />)
            ) : (
              <p className="sepnone">Has not been distributed by another company.</p>
            )}
          </div>
        </div>

        {/*
          The registration a SpinCo files and an ordinary parent does not. A company that
          was distributed long ago can be a parent now, so the date is shown and no verdict
          is drawn from it.
        */}
        <div className="sechd">Own registration</div>
        {c.ownForm10 ? (
          <p>
            Registered its own class under Section 12(b) on{" "}
            <b>{c.ownForm10.filedOn}</b> —{" "}
            <Link to={`/filings/${c.ownForm10.accession}`}>{c.ownForm10.accession}</Link>. A
            company files that when it is being distributed, which does not stop it becoming a
            parent later.
          </p>
        ) : (
          <p>No Form 10 on file. This company has never registered its own class under §12(b).</p>
        )}

        {c.formerNames.length > 0 && (
          <>
            {/* A separation frequently renames the parent, so this is history rather than trivia. */}
            <div className="sechd">Former names</div>
            <div className="filings">
              {c.formerNames.map((n) => (
                <div className="filingrow" key={`${n.name}-${n.from}`}>
                  <div className="filinghd">
                    <span className="filingcaret blank">·</span>
                    <span className="filing">
                      <span className="fd">
                        {n.from?.slice(0, 10)} – {n.to?.slice(0, 10)}
                      </span>
                      <span className="fn">{n.name}</span>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {filings.data && filings.data.items.length > 0 && (
          <>
            <div className="sechd">Filings</div>
            <div className="filings">
              {filings.data.items.map((f) => (
                <div className="filingrow" key={f.accession}>
                  <div className="filinghd">
                    <span className="filingcaret blank">·</span>
                    <Link className="filing" to={`/filings/${f.accession}`}>
                      <span className="ft">{f.formType}</span>
                      <span className="fd">{f.filingDate}</span>
                      <span className="fn">{f.ingestState}</span>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </article>
    </div>
  );
}
